import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database.types';
import toast from 'react-hot-toast';

type Settings = Database['public']['Tables']['settings']['Row'];
type SettingsUpdate = Database['public']['Tables']['settings']['Update'];

interface SettingsStore {
  settings: Settings | null;
  maintenanceMode: boolean;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  lastOperation: (() => Promise<void>) | null;
  loadSettings: () => Promise<void>;
  updateSettings: (settings: SettingsUpdate) => Promise<void>;
  toggleMaintenanceMode: () => Promise<void>;
  clearError: () => void;
  retryOperation: () => Promise<void>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

const DEFAULT_SETTINGS: Settings = {
  id: 1,
  phone: '+90 555 555 55 55',
  email: 'info@example.com',
  address: 'Örnek Adres\nİzmit, Kocaeli',
  business_hours_weekday: 'Pazartesi - Cuma: 09:00 - 18:00',
  business_hours_weekend: 'Cumartesi: 10:00 - 14:00',
  maintenance_mode: false,
  created_at: new Date().toISOString(),
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      settings: null,
      maintenanceMode: false,
      loading: false,
      error: null,
      lastFetch: null,
      lastOperation: null,

      clearError: () => set({ error: null }),

      retryOperation: async () => {
        const lastOp = get().lastOperation;
        if (lastOp) {
          set({ error: null });
          await lastOp();
        }
      },

      loadSettings: async () => {
        const now = Date.now();
        const lastFetch = get().lastFetch;

        // Cache kontrolü
        if (lastFetch && now - lastFetch < CACHE_DURATION) {
          return;
        }

        try {
          set({ loading: true, error: null });
          const { data: settings, error } = await supabase
            .from('settings')
            .select('*')
            .limit(1)
            .maybeSingle();

          if (error) throw error;

          set({
            settings: settings || DEFAULT_SETTINGS,
            maintenanceMode: settings?.maintenance_mode || false,
            lastFetch: now,
          });
        } catch (err) {
          const error = err as Error;
          set({ error: error.message });
          toast.error('Ayarlar yüklenirken bir hata oluştu');
          set({ lastOperation: get().loadSettings });
        } finally {
          set({ loading: false });
        }
      },

      updateSettings: async (newSettings) => {
        const previousSettings = get().settings;
        
        try {
          console.log('Gelen ayarlar:', newSettings);

          // Undefined değerleri temizle
          const cleanSettings = Object.fromEntries(
            Object.entries(newSettings).filter(([_, value]) => value !== undefined)
          );

          // Sadece veritabanında olan alanları al
          const allowedFields = [
            'phone',
            'alternate_phone',
            'email',
            'alternate_email',
            'address',
            'business_hours_weekday',
            'business_hours_weekend',
            'social_media',
            'maintenance_mode'
          ];

          // Veri dönüşümü ve filtreleme
          const sanitizedSettings = Object.fromEntries(
            Object.entries(cleanSettings)
              .map(([key, value]) => {
                // camelCase'den snake_case'e dönüştür
                const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
                return [snakeKey, value];
              })
              .filter(([key]) => allowedFields.includes(key))
          );

          console.log('Dönüştürülmüş ve filtrelenmiş ayarlar:', sanitizedSettings);

          // Optimistik güncelleme
          set(state => ({
            settings: state.settings ? { ...state.settings, ...newSettings } : null,
            loading: true,
            error: null
          }));

          let query = supabase.from('settings');
          
          if (previousSettings?.id) {
            console.log('Mevcut ayarlar güncelleniyor. ID:', previousSettings.id);
            // Mevcut ayarları güncelle
            const { data, error } = await query
              .update(sanitizedSettings)
              .eq('id', previousSettings.id)
              .select()
              .single();

            if (error) {
              console.error('Güncelleme hatası:', error);
              throw error;
            }
            
            console.log('Güncelleme başarılı. Yeni veri:', data);
            set({ 
              settings: data,
              lastFetch: Date.now() 
            });
          } else {
            console.log('Yeni ayar ekleniyor');
            // Yeni ayar ekle
            const { data, error } = await query
              .insert({
                ...DEFAULT_SETTINGS,
                ...sanitizedSettings
              })
              .select()
              .single();

            if (error) {
              console.error('Ekleme hatası:', error);
              throw error;
            }
            
            console.log('Ekleme başarılı. Yeni veri:', data);
            set({ 
              settings: data,
              lastFetch: Date.now() 
            });
          }

          toast.success('Ayarlar başarıyla güncellendi');
        } catch (err) {
          console.error('İşlem hatası:', err);
          // Hata durumunda geri al
          set({ 
            settings: previousSettings,
            error: (err as Error).message 
          });
          toast.error('Ayarlar güncellenirken bir hata oluştu');
          set({ lastOperation: () => get().updateSettings(newSettings) });
        } finally {
          set({ loading: false });
        }
      },

      toggleMaintenanceMode: async () => {
        const currentMode = get().maintenanceMode;
        const previousSettings = get().settings;

        try {
          // Optimistik güncelleme
          set(state => ({
            maintenanceMode: !currentMode,
            settings: state.settings 
              ? { ...state.settings, maintenance_mode: !currentMode }
              : null,
            loading: true,
            error: null
          }));

          const { error } = await supabase
            .from('settings')
            .update({ maintenance_mode: !currentMode })
            .eq('id', get().settings?.id || 1)
            .select()
            .single();

          if (error) throw error;

          set({ lastFetch: Date.now() });
          toast.success(`Bakım modu ${!currentMode ? 'aktif' : 'pasif'} duruma getirildi`);
        } catch (err) {
          // Hata durumunda geri al
          set({ 
            maintenanceMode: currentMode,
            settings: previousSettings,
            error: (err as Error).message 
          });
          toast.error('Bakım modu değiştirilirken bir hata oluştu');
          set({ lastOperation: get().toggleMaintenanceMode });
        } finally {
          set({ loading: false });
        }
      },
    }),
    {
      name: 'settings-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        settings: state.settings,
        maintenanceMode: state.maintenanceMode,
        lastFetch: state.lastFetch,
      }),
    }
  )
);